/** @jsxImportSource @emotion/react */
import { faPencil } from "@fortawesome/pro-regular-svg-icons";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";
import { triggerCheckBusinessRules } from "../../../../../actions/Checks/actions";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import { usePatchCheckMutation } from "../../../../../hooks/mutations";
import { usePatchCheckStateLocally } from "../../../../../hooks/mutations/usePatchCheckMutation";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../../utils";
import MetaForm, { metaFormIsValid } from "../../../../Forms/MetaForm";
import { Button } from "../../../../Helper/Button";
import { useQcEntities } from "../../QcEntityContext";


const PreCheckForm = () => {
    const dispatch = useDispatch();
    const patchCheckMutation = usePatchCheckMutation();
    const [isOpen, toggleModal] = useState(false);
    const { check, layer } = useQcEntities();
    const config = useConfig();
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const location_config = config.get_check_type_config(layer, check);
    const patchCheckStateLocally = usePatchCheckStateLocally();
    const setCheckValue = (field, value, callApi) => {
        patchCheckStateLocally(check, { [field]: value });
        if (callApi) {
            patchCheckMutation.mutate({ check, field, value });
        }
    };


    // * we use this to copy data from the layer to the check e.g. box_size,
    // * or from a computed check_meta value (e.g. the ripening decision proposal).
    const check_meta_form = getFilledArrayOrDefault(location_config?.pre_check_form).map((field) => {
        let mapped = field;
        if (layer[field.default_value_from_layer_key] !== undefined) {
            mapped = { ...mapped, default_value: layer[field.default_value_from_layer_key] };
        }
        if (field.default_value_from_check_meta && check?.[field.default_value_from_check_meta] !== undefined) {
            mapped = { ...mapped, default_value: check[field.default_value_from_check_meta] };
        }
        return mapped;
    });

    if (!isFilledArray(check_meta_form)) {
        return null;
    }

    const canSubmit = useMemo(() => metaFormIsValid(check_meta_form, check, false), [check, check_meta_form]);

    const submit = () => {
        dispatch(triggerCheckBusinessRules(tenantId, check?.check_id) as any);
        toggleModal(false);
    };

    return (
        <>
            <div className="d-inline-flex d-md-block">
                <Button
                    color="secondary"
                    outline
                    onClick={() => toggleModal(true)}
                    icon={faPencil}
                >
                    Edit
                </Button>
            </div>
            <Modal
                isOpen={isOpen}
                size="md"
                toggle={() => toggleModal(!isOpen)}
            >
                <ModalHeader toggle={() => toggleModal(!isOpen)}>
                    {`Check ${check?.check_id}`}
                </ModalHeader>
                <ModalBody>
                    {isFilledArray(check_meta_form) && <MetaForm
                        key="check"
                        meta={check_meta_form}
                        object={check as object}
                        onDebounce={(field, value) => setCheckValue(field, value, true)}
                        setValue={(field, value) => setCheckValue(field, value, false)}
                        extra_context={{ fruit_variety: layer.fruit_variety || undefined, fruit_type: layer.fruit_type || undefined }}
                        config={config} />}

                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => toggleModal(!isOpen)}>
                        Cancel
                    </Button>
                    <Button color="success" disabled={!canSubmit} onClick={submit}>
                        Submit
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default PreCheckForm;

